// Region-entry rules for forced movement and teleport. Pure. No
// React, no Three.js. Encodes the codex rules for what happens when
// a card lands in (or is moved to) a destination cell:
//
//   1. Can it legally land there in its current region? (gate)
//   2. If it lands in an unsurvivable region, what's the consequence?
//      (banish vs die vs no-op)
//
// ── Codex anchors (Forced Movement / Region / Void / Underwater /
//                   Underground / Voidwalk / Submerge / Burrowing /
//                   Airborne / Avatar) ──
//
// Forced Movement: "Unless specified by card text, forced movement
// cannot cause a card to change regions (except for teleportation,
// which can change regions by default)."
//
// Void: "A square without a site is a void. Minions in the void are
// immediately banished unless they have Voidwalk. Avatars in the
// void have a mandatory action." (Avatars don't get banished; they
// get the mandatory site-play action. That case is handled by
// _isMandatorySitePlayPending elsewhere.)
//
// Underwater: "Whenever a minion without Submerge occupies an
// underwater location, it immediately dies. An avatar may never
// enter an underwater location."
//
// Underground: "Whenever a minion without Burrowing occupies an
// underground location, it immediately dies. An avatar may never
// enter an underground location."
//
// Airborne: surface-only benefit; doesn't grant void/water/under
// survival.

import { hasKeyword } from '../core/keywordQuery';

// All region names line up with regions.js + burrowSubmerge.js.
export const REGION = Object.freeze({
  VOID:        'void',
  SURFACE:     'surface',
  UNDERGROUND: 'underground',
  UNDERWATER:  'underwater',
});

/**
 * Decide the region the card occupies BEFORE the move.
 * Reads `card.subsurface` (set by burrowSubmerge.js). Surface units
 * leave it undefined. The 'void' case is implicit (no site at the
 * card's current cell), but we don't compute that here; the caller
 * supplies the source region if it differs from surface/subsurface.
 */
export function currentRegion(card) {
  const sub = card?.subsurface;
  if (sub === 'underground') return REGION.UNDERGROUND;
  if (sub === 'underwater')  return REGION.UNDERWATER;
  return REGION.SURFACE; // includes Voidwalk units on the surface
}

/**
 * Given a movement type ('teleport' | 'forced'), the source's current
 * region, and the destination cell's available regions, decide which
 * region the card lands in. Returns null when the move must fail.
 *
 * Forced movement rules:
 *   - Source region MUST be present at destination.
 *   - If the source is in the void (Voidwalk), forced movement keeps
 *     the unit in the void only if the destination is also a void
 *     square (no site). Otherwise the move fails.
 *
 * Teleport rules:
 *   - Caller may specify the landing region explicitly via
 *     opts.preferredRegion. If absent, teleport defaults to surface
 *     when available, else falls back through underground / underwater
 *     / void in that order (the unit's survival is then resolved by
 *     regionConsequence).
 *   - A teleport may also legally land the unit in a region it can't
 *     survive. The consequence (banish / die) fires after the move.
 */
export function landingRegionForMove(card, sourceRegion, destinationRegions, opts = {}) {
  const moveType = opts.moveType || 'forced';
  if (!destinationRegions || destinationRegions.size === 0) return null;

  if (moveType === 'forced') {
    // Preserve region. If the destination doesn't host it, the move fails.
    if (destinationRegions.has(sourceRegion)) return sourceRegion;
    return null;
  }

  // Teleport path. Honor caller's preferredRegion if the destination
  // hosts it; else pick the safest region the unit can survive in;
  // else fall back to whatever's available (consequence fires next).
  if (opts.preferredRegion && destinationRegions.has(opts.preferredRegion)) {
    return opts.preferredRegion;
  }
  const order = [REGION.SURFACE, REGION.UNDERGROUND, REGION.UNDERWATER, REGION.VOID];
  // First pass: pick a region the card can survive in.
  for (const r of order) {
    if (destinationRegions.has(r) && canSurviveInRegion(card, r)) return r;
  }
  // Fallback: any available region (move still lands; consequence kills).
  for (const r of order) {
    if (destinationRegions.has(r)) return r;
  }
  return null;
}

/**
 * Can this card survive in `region` without immediately dying or
 * being banished? Avatars are a special case. They may NEVER enter
 * non-surface regions per codex, so the only survivable region for
 * an Avatar is surface.
 */
export function canSurviveInRegion(card, region) {
  if (region === REGION.SURFACE) return true;
  if (card?.type === 'Avatar') return false;
  if (region === REGION.VOID)        return hasKeyword(card, 'voidwalk');
  if (region === REGION.UNDERWATER)  return hasKeyword(card, 'submerge');
  if (region === REGION.UNDERGROUND) return hasKeyword(card, 'burrowing');
  return false;
}

/**
 * Post-move consequence. Returns:
 *   'banish'. Send to Banished pile (void without Voidwalk)
 *   'die'. Send to Cemetery (underwater/underground without
 *              the matching survival keyword)
 *   'fail'. Avatar-in-illegal-region; caller must reject the move
 *              entirely (Avatars can't even be temporarily placed)
 *   null. The card survives in this region
 *
 * Per codex, the consequence resolves "immediately" so it isn't a
 * storyline event. It fires as part of the same forced-move event.
 */
export function regionConsequence(card, region) {
  if (canSurviveInRegion(card, region)) return null;
  // Avatars: the rule is harder than "you die." Per codex, an avatar
  // may never enter these regions at all. Treat as a hard rejection
  // of the move (caller fizzles the effect).
  if (card?.type === 'Avatar') return 'fail';
  if (region === REGION.VOID) return 'banish';
  // underwater / underground without keyword → cemetery
  return 'die';
}
